/**
 * useLanguage — single source of truth for the app UI language.
 *
 * - Reads/writes `preferredLanguage` in localStorage.
 * - Calls i18n.changeLanguage so all useTranslation() hooks update immediately.
 * - Dispatches a `languageChanged` window event so non-React code can react.
 * - Any component that calls this hook re-renders when the language changes
 *   (via the storage event listener).
 */
import { useState, useEffect, useCallback } from 'react';
import i18n from '../i18n';

const STORAGE_KEY = 'preferredLanguage';
const DEFAULT_LANG = 'ja';

function getStored() {
  return localStorage.getItem(STORAGE_KEY) || DEFAULT_LANG;
}

export function useLanguage() {
  const [language, setLanguageState] = useState(getStored);

  // Keep in sync when another tab or component changes the value
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue && e.newValue !== language) {
        setLanguageState(e.newValue);
      }
    };
    const onCustom = () => {
      const stored = getStored();
      if (stored !== language) setLanguageState(stored);
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('languageChanged', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('languageChanged', onCustom);
    };
  }, [language]);

  const setLanguage = useCallback((newLang) => {
    if (newLang === language) return;
    localStorage.setItem(STORAGE_KEY, newLang);
    i18n.changeLanguage(newLang);
    setLanguageState(newLang);
    window.dispatchEvent(new Event('languageChanged'));
  }, [language]);

  return [language, setLanguage];
}
